import { useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { styles } from "../styles";
import HighlightText from "../components/HighlightText";
import { hasCapability } from "../utils/capabilities";
import BackButton from "../components/BackButton";
import {
  exportPdf,
  loadImageAsDataUrl,
  printPdf,
  shouldShowPdfChooser,
} from "../utils/pdfExport";
import PdfActionModal from "../components/PdfActionModal";

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Facility-side archive of customer-signed service tickets sent from the
 * contractor (sourced from publicTickets). Click a ticket to open the
 * printable / downloadable copy — both signatures included — intended as
 * the facility's finance / records documentation.
 */
export default function SignedTicketsPage({
  publicTicketsRaw = [],
  customers = [],
  logoUrl = "",
  orgName = "",
  orgAddress = {},
  role,
  capabilities,
  orgId,
  onBack,
}) {
  const [search, setSearch] = useState("");
  const [buildingFilter, setBuildingFilter] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [delErr, setDelErr] = useState("");
  const canDelete = hasCapability(role, capabilities, "deleteSignedTickets");
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected({});
    setDelErr("");
  };
  const deleteTickets = async (ids) => {
    setBulkBusy(true);
    setDelErr("");
    try {
      const fn = httpsCallable(functions, "hidePublicDoc");
      for (const id of ids) {
        await fn({ collection: "publicTickets", docId: id });
      }
      exitSelectMode();
    } catch (e) {
      setDelErr(e.message);
    } finally {
      setBulkBusy(false);
    }
  };

  const buildings = customers.flatMap((c) =>
    (c.locations || []).map((b) => ({ id: b.id, name: b.name, customer: c }))
  );
  const buildingName = (id) => buildings.find((b) => b.id === id)?.name || "—";
  const customerName = (id) => {
    const cust = customers.find((c) => c.id === id);
    return cust?.name || "—";
  };

  const sigName = (sig) => sig?.name || sig?.typedName || "signed";

  const tickets = publicTicketsRaw
    .filter((t) => !(t.hiddenByOrgs || []).includes(orgId))
    .filter((t) => !buildingFilter || t.locationId === buildingFilter)
    .filter((t) =>
      !search.trim()
        ? true
        : (t.summary || "").toLowerCase().includes(search.toLowerCase())
    )
    // Sort by most-recently-signed first. signedAt is an ISO timestamp
    // (set by TicketSignOff at lock time), so plain string compare gives
    // chronological order. Fall back to workDate for legacy docs missing
    // a signedAt, then to "" so anything truly stale sorts last.
    .sort((a, b) => {
      const ak = a.signedAt || a.workDate || "";
      const bk = b.signedAt || b.workDate || "";
      return bk.localeCompare(ak);
    });

  const [pdfPending, setPdfPending] = useState(null);
  const handlePdfPrint = () => {
    if (!pdfPending) return;
    printPdf(pdfPending.doc);
    setPdfPending(null);
  };
  const handlePdfSave = async () => {
    if (!pdfPending) return;
    await exportPdf(pdfPending.doc, pdfPending.filename);
    setPdfPending(null);
  };

  // Customer-facing signed ticket archive (no cost, no markup) with both
  // signatures + photos. Prefers the contractor stamp on the mirror doc so
  // facility viewers see the original brand.
  const printSignedTicket = async (t) => {
    const printLogoUrl = t.contractorLogoUrl || logoUrl;
    const printOrgName = t.contractorOrgName || orgName;
    const printOrgAddress = t.contractorOrgName ? {} : orgAddress;

    const addrLineList = (addr = {}) => {
      const lines = [];
      if (addr.street) lines.push(addr.street);
      const cityLine = [addr.city, addr.state, addr.zip]
        .filter(Boolean)
        .join(", ");
      if (cityLine) lines.push(cityLine);
      if (addr.phone) lines.push(addr.phone);
      return lines;
    };
    const cust = customers.find((c) => c.id === t.customerId);
    const loc = cust?.locations?.find((l) => l.id === t.locationId);
    const locAddrLines = addrLineList({
      street: loc?.street,
      city: loc?.city,
      state: loc?.state,
      zip: loc?.zip,
    });
    const jobNumber = t.jobNumber || "";

    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const PAGE_W = doc.internal.pageSize.getWidth();
    const PAGE_H = doc.internal.pageSize.getHeight();
    const MARGIN = 40;
    const CONTENT_W = PAGE_W - MARGIN * 2;

    // --- Header ---
    const logoData = await loadImageAsDataUrl(printLogoUrl);
    let titleX = MARGIN;
    if (logoData) {
      try {
        doc.addImage(logoData, "AUTO", MARGIN, MARGIN, 80, 60);
        titleX = MARGIN + 90;
      } catch (err) {
        console.warn("logo addImage failed:", err);
      }
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(34, 34, 34);
    doc.text("Service Work Order (Signed)", titleX, MARGIN + 18);

    let headerY = MARGIN + 18;
    if (printOrgName) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(102, 102, 102);
      headerY += 16;
      doc.text(printOrgName, titleX, headerY);
    }
    doc.setFontSize(10);
    doc.setTextColor(85, 85, 85);
    for (const line of addrLineList(printOrgAddress)) {
      headerY += 12;
      doc.text(line, titleX, headerY);
    }
    headerY = Math.max(headerY, MARGIN + 60);

    // --- Doc meta ---
    let y = headerY + 28;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(26, 95, 180);
    doc.text(
      `Job #${jobNumber || "—"}${t.jobTitle ? ` — ${t.jobTitle}` : ""}`,
      MARGIN,
      y
    );
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(34, 34, 34);
    const dateStr = `Date: ${t.workDate || ""}`;
    doc.text(dateStr, PAGE_W - MARGIN - doc.getTextWidth(dateStr), y);

    // --- Customer / Service Location ---
    y += 22;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Customer / Service Location", MARGIN, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.text(customerName(t.customerId), MARGIN, y);
    y += 12;
    doc.text(buildingName(t.locationId), MARGIN, y);
    y += 12;
    for (const line of locAddrLines) {
      doc.text(line, MARGIN, y);
      y += 12;
    }
    y += 6;

    // --- Work performed ---
    if (t.summary) {
      doc.setFont("helvetica", "bold");
      doc.text("Work performed:", MARGIN, y);
      y += 14;
      doc.setFont("helvetica", "normal");
      const sumLines = doc.splitTextToSize(t.summary, CONTENT_W);
      doc.text(sumLines, MARGIN, y);
      y += sumLines.length * 13 + 4;
    }

    // --- Labor table ---
    const ptLabel = (pt) =>
      pt === "overtime" ? "OT" : pt === "double" ? "DT" : "ST";
    const laborBody = (t.labor || []).map((l) => [
      l.employeeName || "—",
      `${num(l.hours)} hr`,
      ptLabel(l.payType),
      l.date || "",
    ]);
    if (laborBody.length === 0) laborBody.push(["None", "", "", ""]);
    autoTable(doc, {
      startY: y + 4,
      head: [["Labor — Employee", "Hours", "Type", "Date"]],
      body: laborBody,
      theme: "plain",
      margin: { left: MARGIN, right: MARGIN },
      styles: { fontSize: 11, cellPadding: 5 },
      headStyles: {
        fontStyle: "bold",
        fillColor: false,
        textColor: 34,
        lineWidth: { bottom: 0.75 },
        lineColor: 200,
      },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { cellWidth: 60 },
        2: { cellWidth: 50 },
        3: { cellWidth: 100 },
      },
    });
    y = doc.lastAutoTable.finalY + 8;

    // --- Materials table ---
    const matBody = (t.materials || []).map((m) => [
      `${m.name || "—"}${m.partNumber ? ` (#${m.partNumber})` : ""}`,
      `${num(m.qty)}`,
    ]);
    if (matBody.length === 0) matBody.push(["None", ""]);
    autoTable(doc, {
      startY: y,
      head: [["Materials", "Qty"]],
      body: matBody,
      theme: "plain",
      margin: { left: MARGIN, right: MARGIN },
      styles: { fontSize: 11, cellPadding: 5 },
      headStyles: {
        fontStyle: "bold",
        fillColor: false,
        textColor: 34,
        lineWidth: { bottom: 0.75 },
        lineColor: 200,
      },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { cellWidth: 80 },
      },
    });
    y = doc.lastAutoTable.finalY + 14;

    // --- Photos (grid, fixed thumb size) ---
    const photoUrls = (t.photos || [])
      .map((p) => (typeof p === "string" ? p : p.url))
      .filter(Boolean);
    if (photoUrls.length) {
      if (y + 30 > PAGE_H - MARGIN) {
        doc.addPage();
        y = MARGIN;
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Photos", MARGIN, y);
      y += 14;
      const THUMB = 120;
      const GAP = 8;
      const PER_ROW = Math.max(
        1,
        Math.floor((CONTENT_W + GAP) / (THUMB + GAP))
      );
      let col = 0;
      let rowH = THUMB;
      for (const url of photoUrls) {
        if (col === 0 && y + rowH > PAGE_H - MARGIN) {
          doc.addPage();
          y = MARGIN;
        }
        const photoData = await loadImageAsDataUrl(url);
        if (photoData) {
          try {
            doc.addImage(
              photoData,
              "AUTO",
              MARGIN + col * (THUMB + GAP),
              y,
              THUMB,
              THUMB
            );
          } catch (err) {
            console.warn("photo addImage failed:", err);
          }
        }
        col++;
        if (col >= PER_ROW) {
          col = 0;
          y += rowH + GAP;
        }
      }
      if (col !== 0) y += rowH + GAP;
      y += 6;
    }

    // --- Signature blocks ---
    const drawSig = (s, label, startY) => {
      let cur = startY;
      if (!s) return cur;
      if (cur + 90 > PAGE_H - MARGIN) {
        doc.addPage();
        cur = MARGIN;
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(34, 34, 34);
      const head = `${label}: ${s.name || ""}${
        s.title ? `, ${s.title}` : ""
      }${s.signedAt ? ` — ${new Date(s.signedAt).toLocaleString()}` : ""}`;
      const headLines = doc.splitTextToSize(head, CONTENT_W);
      doc.text(headLines, MARGIN, cur);
      cur += headLines.length * 13 + 4;
      if (s.image) {
        try {
          doc.addImage(s.image, "PNG", MARGIN, cur, 180, 60);
          cur += 66;
        } catch (err) {
          console.warn("sig addImage failed:", err);
        }
      }
      return cur + 6;
    };
    y = drawSig(t.contractorSignature, "Contractor", y);
    y = drawSig(t.customerSignature, "Facility representative", y);

    const dateSlug = t.workDate || new Date().toISOString().slice(0, 10);
    const jobSlug = jobNumber || (t.id || "").slice(0, 6);
    const filename = `SignedTicket_Job-${jobSlug}_${dateSlug}.pdf`;
    if (shouldShowPdfChooser()) {
      setPdfPending({ doc, filename });
    } else {
      await exportPdf(doc, filename);
    }
  };

  return (
    <>
      <BackButton onClick={onBack} />
      <h2 style={{ margin: "8px 0" }}>Signed Tickets</h2>
      {delErr && (
        <div style={{ color: "red", fontSize: "12px", margin: "4px 0" }}>
          {delErr}
        </div>
      )}
      <p style={{ fontSize: "12px", color: "#666", margin: "0 0 8px" }}>
        Customer-signed service tickets from your contractor. Tap one to open
        the printable / downloadable copy (both signatures included) for your
        records.
      </p>

      <label style={{ fontSize: "12px" }}>Building: </label>
      <select
        value={buildingFilter}
        onChange={(e) => setBuildingFilter(e.target.value)}
      >
        <option value="">All buildings</option>
        {buildings
          .slice()
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
          .map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
      </select>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
          marginTop: "8px",
        }}
      >
        <input
          style={{ ...styles.input, flex: 1, minWidth: "200px", margin: 0 }}
          placeholder="Search work performed..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {canDelete && !selectMode && tickets.length > 0 && (
          <button
            style={{ ...styles.button, margin: 0 }}
            onClick={() => setSelectMode(true)}
            title="Enter selection mode"
          >
            ✓ Select
          </button>
        )}
        {canDelete && selectMode && (
          <>
            <label style={{ cursor: "pointer", fontSize: "13px" }}>
              <input
                type="checkbox"
                checked={
                  tickets.length > 0 && tickets.every((t) => selected[t.id])
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    const next = {};
                    tickets.forEach((t) => (next[t.id] = true));
                    setSelected(next);
                  } else {
                    setSelected({});
                  }
                }}
              />{" "}
              Select All
            </label>
            {(() => {
              const ids = Object.keys(selected).filter((k) => selected[k]);
              return (
                <>
                  <span style={{ fontSize: "13px", color: "#666" }}>
                    {ids.length} selected
                  </span>
                  <button
                    style={{
                      ...styles.button,
                      margin: 0,
                      background: ids.length ? "#fee" : "#f3f4f6",
                      color: ids.length ? "#a00" : "#999",
                    }}
                    disabled={bulkBusy || ids.length === 0}
                    onClick={() => {
                      if (
                        !window.confirm(
                          `Remove ${ids.length} signed ticket(s) from your view? The contractor still keeps their copy.`
                        )
                      )
                        return;
                      deleteTickets(ids);
                    }}
                  >
                    {bulkBusy
                      ? "Removing..."
                      : `Delete Selected${ids.length ? ` (${ids.length})` : ""}`}
                  </button>
                  <button
                    style={{ ...styles.button, margin: 0 }}
                    disabled={bulkBusy}
                    onClick={exitSelectMode}
                  >
                    Done
                  </button>
                </>
              );
            })()}
          </>
        )}
      </div>

      {tickets.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#666" }}>
          No signed tickets yet.
        </p>
      ) : (
        tickets.map((t) => (
          <div
            key={t.id}
            style={{
              ...styles.card,
              cursor: "pointer",
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
            }}
            onClick={() => {
              if (selectMode) {
                setSelected((prev) => {
                  const next = { ...prev };
                  if (next[t.id]) delete next[t.id];
                  else next[t.id] = true;
                  return next;
                });
              } else {
                printSignedTicket(t);
              }
            }}
            title={
              selectMode
                ? "Click to toggle selection"
                : "Click to open the printable signed copy"
            }
          >
            {selectMode && (
              <input
                type="checkbox"
                checked={!!selected[t.id]}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  setSelected((prev) => {
                    const next = { ...prev };
                    if (e.target.checked) next[t.id] = true;
                    else delete next[t.id];
                    return next;
                  })
                }
                style={{ marginTop: "3px" }}
              />
            )}
            <div style={{ flex: 1 }}>
            {/* Display signedAt (when the ticket was finalized) as the row's
                primary date — this matches the sort order and is what's most
                meaningful for an archive view. workDate (when work was done)
                is preserved on the PDF and the detail view. */}
            <strong>
              {t.signedAt
                ? new Date(t.signedAt).toLocaleString()
                : t.workDate || "—"}
            </strong>
            {" — "}
            <span style={{ fontSize: "13px" }}>
              {buildingName(t.locationId)}
            </span>
            <br />
            {t.workDate && t.workDate !== (t.signedAt || "").slice(0, 10) && (
              <span style={{ fontSize: "11px", color: "#888", marginLeft: "8px" }}>
                Work date: {t.workDate}
              </span>
            )}
            <span style={{ fontSize: "12px", color: "#444", marginLeft: "8px" }}>
              <HighlightText text={t.summary || ""} search={search} />
            </span>
            {t.customerSignature && (
              <span style={{ fontSize: "11px", color: "#666", marginLeft: "8px" }}>
                (signed by {sigName(t.customerSignature)})
              </span>
            )}
            <div style={{ fontSize: "11px", color: "#1a5fb4", marginTop: "4px" }}>
              {selectMode ? "Click to toggle selection" : "Click to export →"}
            </div>
            </div>
          </div>
        ))
      )}

      <PdfActionModal
        pending={pdfPending}
        title="Export Signed Ticket"
        onPrint={handlePdfPrint}
        onSave={handlePdfSave}
        onCancel={() => setPdfPending(null)}
      />
    </>
  );
}
